import { useCallback } from "react"
import { DESC } from "helpers/sorts"
import { useFactorViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { useAnalytics } from "connected-components/analytics"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useFactorsViewTableHandlers } from "../../hooks/use-factors-view-table-handlers"
import { BETA_INSTRUMENT_SHAPE, BETA_FIELDS_SHAPE, FACTORS_VIEW_COMMON_PROPS } from "../../prop-types"

export default function FactorsBetaView({ instrumentsWithMetadata, instrumentColumns, betaFields, betaInstrument }) {
  const contextData = useFactorsViewData()
  const { base, sortMetricId, setSortMetricId, sortDirection, setSortDirection, selectedTableLevels, setSelectedTableLevels, isEndVsStartDateComparison, startDate, endDate, instrument, comparisonInstrument, secondInstrument, filter, normalization, modelId, intradayBatchMetadata, intradayPricingAsOf, isIntradayMode } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useFactorsViewTableHandlers({ track: analytics.track })
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDrilldownRowClick = useCallback((factorId, clickPosition) => {
    if (!panelId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "factors.beta",
      title: "Asset View: Beta",
      view: "beta",
      context: {
        modelId,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: { selectedFactor: factorId },
    }))
  }, [panelId, openDrilldown, modelId, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const gatherers = useTemplateGatherers(contextData, "factorBeta", {
    betaInstrument,
    betaFields,
    useCompositionQueries: false,
  })

  const metrics = useFactorViewDashboardMetrics(
    [{ category: "factorBeta" }],
    { instrumentColumns, isActive: !!base, instrumentsWithMetadata, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    modelId={modelId}
    mainInstrument={instrument}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      groupByName: "Category",
      defaultSort: {
        path: `beta${defaultSortSuffix}`,
        direction: DESC,
      },
      disableCategories: true,
      classes: "factors-table",
      idLabel: "Factor",
      hidePagination: true,
      includeDrilldownArrows: true,
      downloadKey: "factors.beta",
      downloadName: "Factors - Beta",
      sortMetricId,
      onSortMetricIdChange: setSortMetricId,
      sortDirection,
      onSortDirectionChange: setSortDirection,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      onDrilldownRowClick: handleDrilldownRowClick,
      viewLabel: "Beta",
    }}
  />
}

FactorsBetaView.propTypes = {
  ...FACTORS_VIEW_COMMON_PROPS,
  betaFields: BETA_FIELDS_SHAPE.isRequired,
  betaInstrument: BETA_INSTRUMENT_SHAPE.isRequired,
}
